import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, formatApiError, CATEGORY_META, timeAgo } from "../lib/api";
import { useAuth } from "../lib/auth";
import {
  TrendingUp,
  Users,
  CheckCircle2,
  DollarSign,
  Flame,
  Wallet,
  Activity,
  Ban,
  RotateCcw,
  Search,
  ShieldAlert,
  Loader2,
} from "lucide-react";

export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [metrics, setMetrics] = useState(null);
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [reports, setReports] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const loadMetrics = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/metrics");
      setMetrics(data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async (q = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users", { params: q ? { search: q } : {} });
      setUsers(data.users || []);
    } finally {
      setLoading(false);
    }
  };

  const loadPayments = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/payments");
      setPayments(data.payments || []);
    } finally {
      setLoading(false);
    }
  };

  const loadWithdrawals = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/withdrawals");
      setWithdrawals(data.withdrawals || []);
    } finally {
      setLoading(false);
    }
  };

  const loadReports = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/reports");
      setReports(data.reports || []);
    } finally {
      setLoading(false);
    }
  };

  const resolveReport = async (rid) => {
    try {
      await api.post(`/admin/reports/${rid}/resolve`);
      loadReports();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => {
    if (tab === "overview") loadMetrics();
    else if (tab === "users") loadUsers(search);
    else if (tab === "payments") loadPayments();
    else if (tab === "withdrawals") loadWithdrawals();
    else if (tab === "reports") loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  if (user && !user.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  const toggleBan = async (u) => {
    if (u.is_admin) return;
    const isBanned = !!u.banned;
    try {
      await api.post(`/admin/users/${u.id}/${isBanned ? "unban" : "ban"}`);
      loadUsers(search);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest font-bold text-[var(--cs-flame)] inline-flex items-center gap-1">
            <ShieldAlert size={14} /> Admin console
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">Mission Control.</h1>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <TabBtn id="overview" tab={tab} setTab={setTab} label="Overview" />
        <TabBtn id="users" tab={tab} setTab={setTab} label="Users" />
        <TabBtn id="payments" tab={tab} setTab={setTab} label="Payments" />
        <TabBtn id="withdrawals" tab={tab} setTab={setTab} label="Withdrawals" />
        <TabBtn id="reports" tab={tab} setTab={setTab} label="Reports" />
      </div>

      {err && (
        <div className="brut-card p-3 bg-red-50 text-red-700 font-semibold text-sm" style={{ boxShadow: "3px 3px 0 0 #b91c1c", borderColor: "#b91c1c" }}>
          {err}
        </div>
      )}

      {tab === "overview" && (
        <Overview metrics={metrics} loading={loading} />
      )}

      {tab === "users" && (
        <UsersTab users={users} search={search} setSearch={setSearch} loadUsers={loadUsers} toggleBan={toggleBan} loading={loading} />
      )}

      {tab === "payments" && (
        <PaymentsTab payments={payments} loading={loading} />
      )}

      {tab === "withdrawals" && (
        <WithdrawalsTab withdrawals={withdrawals} loading={loading} />
      )}

      {tab === "reports" && (
        <ReportsTab reports={reports} resolveReport={resolveReport} loading={loading} />
      )}
    </div>
  );
}

function TabBtn({ id, tab, setTab, label }) {
  return (
    <button
      onClick={() => setTab(id)}
      data-testid={`admin-tab-${id}`}
      className={`brut-btn ${tab === id ? "brut-btn-ink" : ""}`}
    >
      {label}
    </button>
  );
}

function Overview({ metrics, loading }) {
  if (loading || !metrics) {
    return <div className="text-center py-16 text-[var(--cs-ink-2)] font-bold uppercase tracking-widest text-sm">Loading metrics…</div>;
  }
  const s = metrics.summary;
  const tiles = [
    { icon: DollarSign, label: "Platform Revenue", value: `$${s.revenue_total.toFixed(2)}`, sub: `$${s.revenue_week.toFixed(2)} this week`, color: "var(--cs-money-2)" },
    { icon: TrendingUp, label: "GMV (all time)", value: `$${s.gmv_total.toFixed(2)}`, sub: `$${s.gmv_week.toFixed(2)} this week`, color: "var(--cs-blue)" },
    { icon: Users, label: "Total users", value: s.total_users, sub: `+${s.new_users_week} this week`, color: "var(--cs-ink)" },
    { icon: Activity, label: "Active users (7d)", value: s.active_users_week, sub: `${s.new_users_today} new today`, color: "var(--cs-flame)" },
    { icon: CheckCircle2, label: "Tasks completed", value: s.completed_tasks, sub: `${s.completed_week} this week`, color: "var(--cs-money-2)" },
    { icon: Flame, label: "Conversion rate", value: `${s.conversion_rate}%`, sub: "post → complete", color: "var(--cs-flame-2)" },
    { icon: Wallet, label: "Wallet liability", value: `$${s.wallet_balance_owed.toFixed(2)}`, sub: "owed to taskers", color: "var(--cs-ink)" },
    { icon: DollarSign, label: "Avg task price", value: `$${s.avg_task_price.toFixed(2)}`, sub: `${s.open_tasks} open · ${s.claimed_tasks} claimed`, color: "var(--cs-money-2)" },
  ];

  // Build sparkline data
  const ts = metrics.timeseries || [];
  const maxGmv = Math.max(1, ...ts.map((d) => d.gmv));
  const maxUsers = Math.max(1, ...ts.map((d) => d.new_users));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {tiles.map((t, i) => (
          <div key={i} className="brut-card p-4 slide-up" style={{ animationDelay: `${i * 40}ms` }} data-testid={`metric-${i}`}>
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-widest font-bold text-[var(--cs-ink-2)]">{t.label}</span>
              <t.icon size={16} style={{ color: t.color }} />
            </div>
            <div className="font-display tabular text-3xl font-black mt-2 leading-none" style={{ color: t.color }}>
              {t.value}
            </div>
            <div className="text-xs font-semibold text-[var(--cs-ink-2)] mt-2">{t.sub}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Spark title="GMV — last 14 days" data={ts} field="gmv" max={maxGmv} format={(n) => `$${n.toFixed(0)}`} color="var(--cs-money-2)" />
        <Spark title="New users — last 14 days" data={ts} field="new_users" max={maxUsers} format={(n) => `${n}`} color="var(--cs-flame)" />
      </div>

      <div className="brut-card p-5">
        <div className="text-xs uppercase tracking-widest font-bold text-[var(--cs-ink-2)] mb-3">Completed tasks by category</div>
        {(metrics.category_breakdown || []).length === 0 ? (
          <p className="text-[var(--cs-ink-2)] text-sm">No completions yet.</p>
        ) : (
          <div className="space-y-2">
            {metrics.category_breakdown.map((row) => {
              const meta = CATEGORY_META[row.category] || { label: row.category, className: "" };
              const total = metrics.category_breakdown.reduce((a, b) => a + b.gmv, 0);
              const pct = total > 0 ? (row.gmv / total) * 100 : 0;
              return (
                <div key={row.category} className="flex items-center gap-3" data-testid={`cat-row-${row.category}`}>
                  <span className={`brut-chip ${meta.className}`}>{meta.label}</span>
                  <div className="flex-1 h-3 bg-slate-200 rounded-full overflow-hidden border-2" style={{ borderColor: "var(--cs-ink)" }}>
                    <div className="h-full" style={{ width: `${pct}%`, background: "var(--cs-ink)" }} />
                  </div>
                  <span className="font-bold tabular text-sm w-24 text-right">${row.gmv.toFixed(0)} · {row.count}x</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function Spark({ title, data, field, max, format, color }) {
  const w = 100;
  const h = 40;
  const step = data.length > 1 ? w / (data.length - 1) : 0;
  const pts = data.map((d, i) => {
    const y = h - (d[field] / max) * h;
    return `${i * step},${y.toFixed(2)}`;
  }).join(" ");
  const last = data[data.length - 1]?.[field] ?? 0;
  return (
    <div className="brut-card p-5">
      <div className="text-xs uppercase tracking-widest font-bold text-[var(--cs-ink-2)]">{title}</div>
      <div className="mt-2 flex items-end gap-3">
        <div className="font-display tabular text-4xl font-black leading-none" style={{ color }}>{format(last)}</div>
        <span className="text-xs font-semibold text-[var(--cs-ink-2)] mb-1">today</span>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full mt-3" preserveAspectRatio="none" height="80">
        <polyline fill="none" stroke={color} strokeWidth="2" points={pts} vectorEffect="non-scaling-stroke" />
        <polyline fill={color} fillOpacity="0.15" stroke="none" points={`0,${h} ${pts} ${w},${h}`} />
      </svg>
    </div>
  );
}

function UsersTab({ users, search, setSearch, loadUsers, toggleBan, loading }) {
  return (
    <div className="space-y-4">
      <div className="relative max-w-md">
        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--cs-ink-2)]" />
        <input
          data-testid="admin-user-search"
          className="brut-input pl-11"
          placeholder="Search by name or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && loadUsers(search)}
        />
      </div>
      {loading ? (
        <div className="text-center py-10 text-[var(--cs-ink-2)] font-bold uppercase tracking-widest text-sm">Loading…</div>
      ) : (
        <div className="brut-card overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[var(--cs-ink)] text-white text-left">
              <tr>
                <Th>Name</Th><Th>Email</Th><Th>Earned</Th><Th>Done</Th><Th>Streak</Th><Th>Joined</Th><Th>Status</Th><Th>Action</Th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b-2" style={{ borderColor: "var(--cs-ink)" }} data-testid={`admin-user-${u.id}`}>
                  <Td><span className="font-bold">{u.name}</span> {u.is_admin && <span className="brut-chip bg-[var(--cs-flame)] text-white ml-2">Admin</span>}</Td>
                  <Td className="text-[var(--cs-ink-2)]">{u.email}</Td>
                  <Td className="tabular font-bold">${(u.total_earned || 0).toFixed(2)}</Td>
                  <Td className="tabular">{u.tasks_completed || 0}</Td>
                  <Td className="tabular">{u.streak_days || 0}</Td>
                  <Td className="text-[var(--cs-ink-2)] text-xs">{timeAgo(u.created_at)}</Td>
                  <Td>
                    {u.banned ? (
                      <span className="brut-chip bg-red-200">Banned</span>
                    ) : (
                      <span className="brut-chip bg-[var(--cs-money)]">Active</span>
                    )}
                  </Td>
                  <Td>
                    {!u.is_admin && (
                      <button
                        onClick={() => toggleBan(u)}
                        data-testid={`ban-toggle-${u.id}`}
                        className={`brut-btn !px-3 !py-1.5 text-xs ${u.banned ? "brut-btn-money" : ""}`}
                        style={!u.banned ? { background: "#fee2e2", borderColor: "#b91c1c", color: "#b91c1c", boxShadow: "3px 3px 0 0 #b91c1c" } : {}}
                      >
                        {u.banned ? <><RotateCcw size={12} /> Unban</> : <><Ban size={12} /> Ban</>}
                      </button>
                    )}
                  </Td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr><td colSpan={8} className="text-center py-10 text-[var(--cs-ink-2)]">No users found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PaymentsTab({ payments, loading }) {
  if (loading) return <div className="text-center py-10 text-[var(--cs-ink-2)] font-bold uppercase tracking-widest text-sm">Loading…</div>;
  return (
    <div className="brut-card overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-[var(--cs-ink)] text-white text-left">
          <tr><Th>Task</Th><Th>Poster</Th><Th>Amount</Th><Th>Status</Th><Th>When</Th></tr>
        </thead>
        <tbody>
          {payments.map((p) => (
            <tr key={p.session_id} className="border-b-2" style={{ borderColor: "var(--cs-ink)" }} data-testid={`payment-${p.session_id}`}>
              <Td className="font-bold">{p.task_title}</Td>
              <Td className="text-[var(--cs-ink-2)]">{p.poster_name}<br /><span className="text-xs">{p.poster_email}</span></Td>
              <Td className="tabular font-bold">${(p.amount || 0).toFixed(2)}</Td>
              <Td><PaymentStatusChip s={p.status} /></Td>
              <Td className="text-xs text-[var(--cs-ink-2)]">{timeAgo(p.created_at)}</Td>
            </tr>
          ))}
          {payments.length === 0 && (
            <tr><td colSpan={5} className="text-center py-10 text-[var(--cs-ink-2)]">No payments yet</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function WithdrawalsTab({ withdrawals, loading }) {
  if (loading) return <div className="text-center py-10 text-[var(--cs-ink-2)] font-bold uppercase tracking-widest text-sm">Loading…</div>;
  return (
    <div className="brut-card overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-[var(--cs-ink)] text-white text-left">
          <tr><Th>User</Th><Th>Amount</Th><Th>Transfer ID</Th><Th>When</Th></tr>
        </thead>
        <tbody>
          {withdrawals.map((w) => (
            <tr key={w.id} className="border-b-2" style={{ borderColor: "var(--cs-ink)" }} data-testid={`wd-${w.id}`}>
              <Td className="font-bold">{w.user_name}<br /><span className="text-xs text-[var(--cs-ink-2)]">{w.user_email}</span></Td>
              <Td className="tabular font-bold">${(w.amount || 0).toFixed(2)}</Td>
              <Td className="text-xs text-[var(--cs-ink-2)] font-mono-num">{w.transfer_id}</Td>
              <Td className="text-xs text-[var(--cs-ink-2)]">{timeAgo(w.created_at)}</Td>
            </tr>
          ))}
          {withdrawals.length === 0 && (
            <tr><td colSpan={4} className="text-center py-10 text-[var(--cs-ink-2)]">No withdrawals yet</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function ReportsTab({ reports, resolveReport, loading }) {
  if (loading) return <div className="text-center py-10 text-[var(--cs-ink-2)] font-bold uppercase tracking-widest text-sm">Loading…</div>;
  return (
    <div className="brut-card overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-[var(--cs-ink)] text-white text-left">
          <tr><Th>Task</Th><Th>Reported user</Th><Th>Reason</Th><Th>Details</Th><Th>Reporter</Th><Th>When</Th><Th>Status</Th><Th>Action</Th></tr>
        </thead>
        <tbody>
          {reports.map((r) => (
            <tr key={r.id} className="border-b-2" style={{ borderColor: "var(--cs-ink)" }} data-testid={`report-row-${r.id}`}>
              <Td className="font-bold">{r.task_title}</Td>
              <Td>{r.reported_user_name}</Td>
              <Td><span className="brut-chip bg-red-200">{r.reason}</span></Td>
              <Td className="text-xs text-[var(--cs-ink-2)] max-w-[220px]">{r.details || "—"}</Td>
              <Td className="text-[var(--cs-ink-2)]">{r.reporter_name}</Td>
              <Td className="text-xs text-[var(--cs-ink-2)]">{timeAgo(r.created_at)}</Td>
              <Td>
                {r.status === "resolved" ? (
                  <span className="brut-chip bg-[var(--cs-money)]">Resolved</span>
                ) : (
                  <span className="brut-chip bg-[var(--cs-yellow)]">Open</span>
                )}
              </Td>
              <Td>
                {r.status !== "resolved" && (
                  <button
                    onClick={() => resolveReport(r.id)}
                    data-testid={`resolve-report-${r.id}`}
                    className="brut-btn brut-btn-money !px-3 !py-1.5 text-xs"
                  >
                    Resolve
                  </button>
                )}
              </Td>
            </tr>
          ))}
          {reports.length === 0 && (
            <tr><td colSpan={8} className="text-center py-10 text-[var(--cs-ink-2)]">No reports — all clear 🎉</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function PaymentStatusChip({ s }) {
  const map = {
    paid: { label: "Paid", bg: "var(--cs-money)" },
    initiated: { label: "Pending", bg: "var(--cs-yellow)" },
    refunded: { label: "Refunded", bg: "#94A3B8", fg: "#fff" },
    refund_unavailable: { label: "Refund N/A", bg: "#FCA5A5" },
    open: { label: "Open", bg: "var(--cs-yellow)" },
    expired: { label: "Expired", bg: "#94A3B8", fg: "#fff" },
  };
  const m = map[s] || { label: s || "—", bg: "#E2E8F0" };
  return <span className="brut-chip" style={{ background: m.bg, color: m.fg || "var(--cs-ink)" }}>{m.label}</span>;
}

function Th({ children }) { return <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-bold">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-3 py-3 ${className}`}>{children}</td>; }
